import type { AwsRum, AwsRumConfig } from "aws-rum-web";
import type { CloudWatchRumErrorSource } from "@/lib/client/cloudwatch-rum-error-source";
import {
  buildSafeApiUrlPatterns,
  buildSensitiveApiUrlPatterns,
  getSafeRumPageTitle,
  isSafeApiUrl,
  toSafeRumApiFamily,
  toSafeRumPageId,
} from "@/lib/client/cloudwatch-rum-route";
import { APP_AWS_REGION } from "@/lib/config/platform";
import {
  CLIENT_ERROR_CUSTOM_MESSAGE,
  CLIENT_ERROR_REPORTED_FRAME_LIMIT,
  sanitizeClientErrorStackFrame,
  toClientErrorFilename,
  toClientErrorMessageTemplate,
  toClientErrorPosition,
} from "@/lib/shared/client-error-sanitizer";
import { isSafeObservabilityRelease } from "@/lib/shared/observability-release";

const JS_ERROR_EVENT_TYPE = "com.amazon.rum.js_error_event";
const SAFE_WEB_VITAL_EVENT_TYPES = {
  CLS: "com.amazon.rum.cumulative_layout_shift_event",
  INP: "com.amazon.rum.interaction_to_next_paint_event",
  LCP: "com.amazon.rum.largest_contentful_paint_event",
} as const;
const MAX_METADATA_VALUE_LENGTH = 256;
const MAX_QUEUED_ERRORS = 10;
const MAX_SAFE_STACK_FRAMES = 20;
const SLOW_API_RESPONSE_MS = 5_000;
const SAFE_CORRELATION_ID_PATTERN = /^[A-Za-z0-9_.:-]+$/;
const RESIZE_OBSERVER_LOOP_MESSAGE =
  /^ResizeObserver loop (?:completed with undelivered notifications|limit exceeded)\.?$/;
const OPAQUE_SCRIPT_ERROR_MESSAGE = /^Script error\.?$/;
const SAFE_ERROR_NAMES = new Set([
  "AbortError",
  "AggregateError",
  "ApiClientError",
  "ChunkLoadError",
  "ConstraintError",
  "DataError",
  "EncodingError",
  "Error",
  "EvalError",
  "InvalidStateError",
  "NetworkError",
  "NotAllowedError",
  "NotFoundError",
  "NotReadableError",
  "OperationError",
  "QuotaExceededError",
  "RangeError",
  "ReferenceError",
  "SyntaxError",
  "TimeoutError",
  "TypeError",
  "URIError",
]);
const SUPPRESSED_ERROR_NAMES = new Set(["AbortError", "ChunkLoadError"]);

const PUBLIC_RUM_ENV = {
  applicationId: process.env["NEXT_PUBLIC_CLOUDWATCH_RUM_APPLICATION_ID"],
  identityPoolId: process.env["NEXT_PUBLIC_CLOUDWATCH_RUM_IDENTITY_POOL_ID"],
  releaseId: process.env["NEXT_PUBLIC_APP_RELEASE_ID"],
};

type RumClient = AwsRum;
type EventMetadata = Record<string, string | number | boolean>;

export type CloudWatchRumPublicEnvironment = {
  applicationId?: string | undefined;
  identityPoolId?: string | undefined;
  region?: string | undefined;
  releaseId?: string | undefined;
};

export type CloudWatchRumRuntimeConfig = {
  applicationId: string;
  identityPoolId: string;
  region: string;
  releaseId?: string;
};

export type CloudWatchRumActor = {
  userCorrelationId: string;
  organizationCorrelationId?: string;
  role: string;
  tenantType?: string;
};

type PendingError = {
  error: Error;
  metadata: EventMetadata;
};

type ErrorEventDetail = Pick<
  ErrorEvent,
  "colno" | "filename" | "lineno" | "message"
>;

type SafeClientError = {
  name: string;
  messageTemplate?: string;
  frames: string[];
  location?: { filename: string; lineno?: number; colno?: number };
};

type SafeWebVitalMetric = {
  name: keyof typeof SAFE_WEB_VITAL_EVENT_TYPES;
  value: number;
};

let rumClient: RumClient | undefined;
let rumStopped = false;
let uninstallApiCorrelationCapture: (() => void) | undefined;
let initialization: Promise<RumClient | undefined> | undefined;
let currentActor: CloudWatchRumActor | undefined;
let pendingErrorMetadata: EventMetadata | undefined;
const pendingErrors: PendingError[] = [];
const clientErrorIds = new WeakMap<object, string>();
const reportedClientErrorContexts = new WeakMap<object, Set<string>>();

function nonEmpty(value: string | undefined) {
  const normalized = value?.trim();
  return normalized ? normalized : undefined;
}

function safeMetadataValue(value: unknown) {
  if (typeof value !== "string") return undefined;
  const normalized = value.trim();
  if (!normalized || normalized.length > MAX_METADATA_VALUE_LENGTH) {
    return undefined;
  }
  return normalized;
}

function safeCorrelationId(value: unknown) {
  const normalized = safeMetadataValue(value);
  return normalized && SAFE_CORRELATION_ID_PATTERN.test(normalized)
    ? normalized
    : undefined;
}

function createClientErrorId() {
  try {
    const randomUuid = globalThis.crypto?.randomUUID?.();
    if (randomUuid) return `client_${randomUuid}`;
  } catch {
    // Correlation IDs must not affect application error handling.
  }
  return `client_${Date.now().toString(36)}_${Math.random()
    .toString(36)
    .slice(2, 10)}`;
}

function resolveClientErrorId(error: unknown, requestedErrorId: unknown) {
  const requested = safeCorrelationId(requestedErrorId);
  if (error === null || typeof error !== "object") {
    return requested ?? createClientErrorId();
  }

  if (requested) {
    clientErrorIds.set(error, requested);
    return requested;
  }
  const existing = clientErrorIds.get(error);
  if (existing) return existing;
  const resolved = createClientErrorId();
  clientErrorIds.set(error, resolved);
  return resolved;
}

export function getOrCreateCloudWatchRumErrorId(
  error: unknown,
  requestedErrorId?: string,
) {
  if (error !== null && typeof error === "object") {
    const existing = clientErrorIds.get(error);
    if (existing) return existing;
  }
  return resolveClientErrorId(error, requestedErrorId);
}

function safeObservabilityCorrelationId(
  kind: "user" | "organization",
  value: unknown,
) {
  const normalized = safeMetadataValue(value);
  return normalized &&
    new RegExp(`^obs_${kind}_[A-Za-z0-9_-]{22}$`).test(normalized)
    ? normalized
    : undefined;
}

export function resolveCloudWatchRumRuntimeConfig(
  environment: CloudWatchRumPublicEnvironment = PUBLIC_RUM_ENV,
): CloudWatchRumRuntimeConfig | undefined {
  const applicationId = nonEmpty(environment.applicationId);
  const identityPoolId = nonEmpty(environment.identityPoolId);
  if (!applicationId || !identityPoolId) return undefined;

  const releaseId = nonEmpty(environment.releaseId);
  return {
    applicationId,
    identityPoolId,
    region: APP_AWS_REGION,
    ...(isSafeObservabilityRelease(releaseId) ? { releaseId } : {}),
  };
}

function getRequestId(value: unknown, depth = 0): string | undefined {
  if (depth > 1 || value === null || typeof value !== "object") {
    return undefined;
  }

  const record = value as Record<string, unknown>;
  const requestId = safeCorrelationId(record["requestId"]);
  if (requestId) return requestId;
  return getRequestId(record["cause"], depth + 1);
}

function getSafeErrorName(error: unknown) {
  if (error === null || typeof error !== "object") return "Error";
  const name = (error as Record<string, unknown>)["name"];
  return typeof name === "string" && SAFE_ERROR_NAMES.has(name)
    ? name
    : "Error";
}

function getErrorStack(error: unknown) {
  if (error === null || typeof error !== "object") return undefined;
  const stack = (error as Record<string, unknown>)["stack"];
  return typeof stack === "string" ? stack : undefined;
}

function getRawErrorMessage(error: unknown, event?: ErrorEventDetail) {
  if (typeof error === "string") return error;
  if (error !== null && typeof error === "object") {
    const message = (error as Record<string, unknown>)["message"];
    if (typeof message === "string") return message;
  }
  return event?.message;
}

function describeClientError(
  error: unknown,
  event?: ErrorEventDetail,
): SafeClientError {
  const messageTemplate = toClientErrorMessageTemplate(
    getRawErrorMessage(error, event),
  );
  const frames = (getErrorStack(error)?.split("\n") ?? [])
    .flatMap((line) => sanitizeClientErrorStackFrame(line) ?? [])
    .slice(0, MAX_SAFE_STACK_FRAMES);
  const lineno = toClientErrorPosition(event?.lineno);
  const colno = toClientErrorPosition(event?.colno);
  return {
    name: getSafeErrorName(error),
    ...(messageTemplate ? { messageTemplate } : {}),
    frames,
    ...(event
      ? {
          location: {
            filename: toClientErrorFilename(
              event.filename,
              window.location.origin,
            ),
            ...(lineno === undefined ? {} : { lineno }),
            ...(colno === undefined ? {} : { colno }),
          },
        }
      : {}),
  };
}

function toCloudWatchRumError(description: SafeClientError) {
  const template = description.messageTemplate;
  const sanitized = new Error(
    template && template !== CLIENT_ERROR_CUSTOM_MESSAGE
      ? template
      : description.name,
  );
  sanitized.name = description.name;
  // V8's anonymous frame form keeps the stack parseable for source maps.
  if (description.frames.length)
    sanitized.stack = description.frames
      .map((location) => `at ${location}`)
      .join("\n");
  else delete sanitized.stack;
  // aws-rum-web copies these Firefox-style properties into the js_error
  // event's filename, lineno, and colno fields.
  if (description.location) {
    Object.assign(sanitized, {
      fileName: description.location.filename,
      lineNumber: description.location.lineno,
      columnNumber: description.location.colno,
    });
  }
  return sanitized;
}

export function sanitizeCloudWatchRumError(
  error: unknown,
  event?: ErrorEventDetail,
) {
  return toCloudWatchRumError(describeClientError(error, event));
}

function recordRumError(
  client: RumClient,
  error: Error,
  metadata: EventMetadata,
) {
  pendingErrorMetadata = metadata;
  try {
    client.recordError(error);
  } catch {
    // Observability must never change application error handling.
  } finally {
    if (pendingErrorMetadata === metadata) pendingErrorMetadata = undefined;
  }
}

function recordSanitizedError(
  description: SafeClientError,
  metadata: EventMetadata,
) {
  if (rumStopped || SUPPRESSED_ERROR_NAMES.has(description.name)) return;
  const sanitized = toCloudWatchRumError(description);
  if (!rumClient) {
    if (pendingErrors.length >= MAX_QUEUED_ERRORS) pendingErrors.shift();
    pendingErrors.push({ error: sanitized, metadata });
    return;
  }
  recordRumError(rumClient, sanitized, metadata);
}

// Browser notifications that carry no actionable error: ResizeObserver loop
// warnings and cross-origin "Script error." without an exception or location.
function isBenignWindowError(event: ErrorEvent) {
  const message = typeof event.message === "string" ? event.message.trim() : "";
  if (RESIZE_OBSERVER_LOOP_MESSAGE.test(message)) return true;
  return (
    event.error == null &&
    OPAQUE_SCRIPT_ERROR_MESSAGE.test(message) &&
    !event.filename &&
    !event.lineno
  );
}

function captureWindowError(event: ErrorEvent) {
  if (isBenignWindowError(event)) return;
  captureCloudWatchRumError(
    event.error,
    { handled: false, source: "window_error" },
    event,
  );
}

function captureUnhandledRejection(event: PromiseRejectionEvent) {
  captureCloudWatchRumError(event.reason, {
    handled: false,
    source: "unhandled_rejection",
  });
}

// Installed by the application instead of the SDK's JsErrorPlugin handlers so
// the telemetry route keeps receiving errors after RUM has been stopped.
function installGlobalErrorCapture() {
  window.addEventListener("error", captureWindowError);
  window.addEventListener("unhandledrejection", captureUnhandledRejection);
}

/**
 * Stops RUM for the rest of the page after its first network or credential
 * failure (for example an ad blocker). The failure itself is not reported.
 */
function stopCloudWatchRum() {
  if (rumStopped) return;
  rumStopped = true;
  pendingErrors.length = 0;
  uninstallApiCorrelationCapture?.();
  uninstallApiCorrelationCapture = undefined;
  const client = rumClient;
  rumClient = undefined;
  try {
    client?.disable();
  } catch {
    // Runs inside a rejection handler; throwing here would leak a rejection.
  }
}

/**
 * The dataplane fetch used by the SDK. The first rejection (network failure or
 * blocking) stops RUM so the SDK's retries and 5-second dispatch timer do not
 * keep calling a blocked endpoint; the SDK still receives and handles the
 * rejection. HTTP error responses resolve and stay with the SDK: on a 403 it
 * purges its cached credentials and fetches new ones. Without identityPoolId
 * in its config it keeps the cached Cognito identity, so drop that here first
 * to obtain a new identity as the SDK's own Cognito flow does.
 */
function createContainedRumFetch(
  identityStorageKey: string,
): NonNullable<AwsRumConfig["fetchFunction"]> {
  const fetchFunction = window.fetch;
  return (input, init) => {
    if (rumStopped) return Promise.reject(new Error("CloudWatch RUM stopped"));
    const response = fetchFunction.call(window, input, init);
    response.then((result) => {
      if (result.status !== 403) return;
      try {
        localStorage.removeItem(identityStorageKey);
      } catch {
        // Storage can be unavailable; the SDK then has no identity cached.
      }
    }, stopCloudWatchRum);
    return response;
  };
}

function getActorMetadata(): EventMetadata {
  if (!currentActor) return {};
  return {
    user_correlation_id: currentActor.userCorrelationId,
    ...(currentActor.organizationCorrelationId
      ? {
          organization_correlation_id: currentActor.organizationCorrelationId,
        }
      : {}),
    role: currentActor.role,
    ...(currentActor.tenantType
      ? { tenant_type: currentActor.tenantType }
      : {}),
  };
}

function consumeErrorMetadata(eventType: string) {
  if (eventType !== JS_ERROR_EVENT_TYPE || !pendingErrorMetadata) return {};
  const metadata = pendingErrorMetadata;
  pendingErrorMetadata = undefined;
  return metadata;
}

function setRumMetadataHook(client: RumClient) {
  client.setEventMetadataHook((eventType) => ({
    ...getActorMetadata(),
    ...consumeErrorMetadata(eventType),
  }));
}

function startRumSession(client: RumClient) {
  // CloudWatch RUM UserDetails only accepts UUIDs generated by the SDK.
  // Stable application actor IDs remain searchable in event metadata.
  client.startSession();
}

export function recordSafeWebVital(
  client: Pick<RumClient, "recordEvent">,
  metric: SafeWebVitalMetric,
) {
  if (!Number.isFinite(metric.value) || metric.value < 0) return;
  try {
    client.recordEvent(SAFE_WEB_VITAL_EVENT_TYPES[metric.name], {
      version: "1.0.0",
      value: metric.value,
    });
  } catch {
    // Performance telemetry must never affect application behavior.
  }
}

async function installSafeWebVitalsCapture(client: RumClient) {
  // The attribution build used by the stock RUM performance telemetry adds
  // LCP resource URLs and DOM selectors. The base web-vitals callbacks retain
  // the diagnostic values without those customer-controlled strings.
  const { onCLS, onINP, onLCP } = await import("web-vitals");
  onCLS((metric) => recordSafeWebVital(client, metric));
  onINP((metric) => recordSafeWebVital(client, metric));
  onLCP((metric) => recordSafeWebVital(client, metric));
}

function recordCurrentPage(client: RumClient, pathname: string) {
  const pageId = toSafeRumPageId(pathname);
  client.recordPageView({
    pageId,
    pageAttributes: { title: getSafeRumPageTitle(pageId) },
  });
}

function recordPendingErrors(client: RumClient) {
  for (const pending of pendingErrors.splice(0)) {
    recordRumError(client, pending.error, pending.metadata);
  }
}

function requestToUrl(input: RequestInfo | URL) {
  if (input instanceof Request) return input.url;
  return input.toString();
}

function requestMethod(input: RequestInfo | URL, init?: RequestInit) {
  return (init?.method ?? (input instanceof Request ? input.method : "GET"))
    .toUpperCase()
    .slice(0, 16);
}

function requestSignal(input: RequestInfo | URL, init?: RequestInit) {
  return init?.signal ?? (input instanceof Request ? input.signal : undefined);
}

function isCodexEventStreamUrl(url: string, origin: string) {
  try {
    const expectedOrigin = new URL(origin).origin;
    const parsed = new URL(url, expectedOrigin);
    return (
      parsed.origin === expectedOrigin &&
      /^\/api\/conversations\/[^/]+\/(?:events|turns)$/.test(parsed.pathname)
    );
  } catch {
    return false;
  }
}

export function installApiCorrelationCapture(
  client: Pick<RumClient, "recordEvent">,
) {
  const originalFetch = window.fetch;
  const origin = window.location.origin;

  const observedFetch: typeof window.fetch = async (
    input: RequestInfo | URL,
    init?: RequestInit,
  ) => {
    const url = requestToUrl(input);
    const startedAt = performance.now();
    let response: Response;
    try {
      response = await originalFetch.call(window, input, init);
    } catch (error) {
      try {
        const wasAborted =
          requestSignal(input, init)?.aborted ||
          getSafeErrorName(error) === "AbortError";
        if (!wasAborted && isSafeApiUrl(url, origin)) {
          const apiFamily = toSafeRumApiFamily(url, origin);
          const durationMs = Math.max(
            0,
            Math.round(performance.now() - startedAt),
          );
          const errorType = getSafeErrorName(error);
          client.recordEvent(
            "sozo.api_correlation",
            {
              version: "1.0.0",
              api_family: apiFamily,
              duration_ms: durationMs,
              error_type: errorType,
              reason: "network_error",
            },
            {
              api_family: apiFamily,
              correlation_reason: "network_error",
              error_type: errorType,
            },
          );
        }
      } catch {
        // Telemetry must never replace the original network failure.
      }
      throw error;
    }
    try {
      const durationMs = Math.max(0, Math.round(performance.now() - startedAt));
      const requestId = safeCorrelationId(response.headers.get("x-request-id"));
      const isOperationalFailure =
        response.status >= 500 || response.status === 429;
      const isSlow =
        durationMs >= SLOW_API_RESPONSE_MS &&
        !isCodexEventStreamUrl(url, origin);

      if (
        requestId &&
        isSafeApiUrl(url, origin) &&
        (isOperationalFailure || isSlow)
      ) {
        const apiFamily = toSafeRumApiFamily(url, origin);
        const reason = isOperationalFailure ? "http_error" : "slow_response";
        client.recordEvent(
          "sozo.api_correlation",
          {
            version: "1.0.0",
            api_family: apiFamily,
            duration_ms: durationMs,
            method: requestMethod(input, init),
            reason,
            request_id: requestId,
            status: response.status,
          },
          {
            api_family: apiFamily,
            correlation_reason: reason,
            request_id: requestId,
          },
        );
      }
    } catch {
      // Telemetry enrichment must not turn a successful fetch into a failure.
    }

    return response;
  };
  window.fetch = observedFetch;

  return () => {
    if (window.fetch === observedFetch) window.fetch = originalFetch;
  };
}

export function buildCloudWatchRumSdkConfig(
  runtime: CloudWatchRumRuntimeConfig,
  origin: string,
): AwsRumConfig {
  const safeApiUrlPatterns = buildSafeApiUrlPatterns(origin);
  const sensitiveApiUrlPatterns = buildSensitiveApiUrlPatterns(origin);
  return {
    allowCookies: false,
    disableAutoPageView: true,
    enableW3CTraceId: true,
    enableXRay: true,
    // identityPoolId is deliberately absent: createCloudWatchRumClient sets
    // the Cognito enhanced-flow credentials itself so failures stay handled.
    // aws-rum-web currently merges its us-west-2 default endpoint before the
    // regional client is created. Pin the dataplane to the app monitor region.
    endpoint: `https://dataplane.rum.${runtime.region}.amazonaws.com`,
    recordResourceUrl: false,
    sessionSampleRate: 1,
    signing: true,
    telemetries: [
      // Loaded only for recordError(). Automatic window errors are captured,
      // sanitized, and recorded by installGlobalErrorCapture instead.
      ["errors", { ignore: () => true }],
      [
        "http",
        {
          addXRayTraceIdHeader: safeApiUrlPatterns,
          recordAllRequests: true,
          urlsToExclude: sensitiveApiUrlPatterns,
          urlsToInclude: safeApiUrlPatterns,
        },
      ],
    ],
    ...(runtime.releaseId ? { releaseId: runtime.releaseId } : {}),
  };
}

async function createCloudWatchRumClient() {
  if (typeof window === "undefined") return undefined;
  const runtime = resolveCloudWatchRumRuntimeConfig();
  if (!runtime) return undefined;

  installGlobalErrorCapture();
  const [
    { AwsRum, NavigationPlugin, ResourcePlugin },
    { EnhancedAuthentication },
    { IDENTITY_KEY },
  ] = await Promise.all([
    import("aws-rum-web"),
    import("@aws-rum/web-core"),
    import("@aws-rum/web-core/utils/constants"),
  ]);
  const config = {
    ...buildCloudWatchRumSdkConfig(runtime, window.location.origin),
    // Preserve URL-free navigation/resource timings while replacing the stock
    // attribution-bearing WebVitalsPlugin with safe value-only callbacks.
    eventPluginsToLoad: [new NavigationPlugin(), new ResourcePlugin()],
    // cookieAttributes.unique is false (the SDK default), so web-core stores
    // the identity under the bare IDENTITY_KEY.
    fetchFunction: createContainedRumFetch(IDENTITY_KEY),
  };

  const client = new AwsRum(
    runtime.applicationId,
    runtime.releaseId ?? "unversioned",
    runtime.region,
    config,
  );
  // aws-rum-web 3.1.0 prefetches Cognito credentials and discards the promise
  // (web-core Dispatch.setAwsCredentials), so a blocked Cognito request became
  // an unhandled rejection reported as an application error. Supply the same
  // enhanced-flow provider (no guestRoleArn; the pool keeps AllowClassicFlow
  // disabled) ourselves and handle every promise it returns, which also covers
  // the discarded prefetch. A fresh instance per call skips the in-memory
  // cache, so after a 403 Dispatch.forceRebuildClient clears localStorage and
  // the next call fetches new credentials, as the SDK's own factory does.
  // unique: false matches the SDK's credential storage key.
  client.setAwsCredentials(() => {
    const credentials = new EnhancedAuthentication(
      {
        identityPoolId: runtime.identityPoolId,
        cookieAttributes: { unique: false },
      },
      runtime.applicationId,
    ).ChainAnonymousCredentialsProvider();
    credentials.catch(stopCloudWatchRum);
    return credentials;
  });
  rumClient = client;
  setRumMetadataHook(client);
  uninstallApiCorrelationCapture = installApiCorrelationCapture(client);
  void installSafeWebVitalsCapture(client).catch(() => undefined);
  if (currentActor) startRumSession(client);
  recordCurrentPage(client, window.location.pathname);
  try {
    client.recordEvent("sozo.rum_initialized", {
      schema_version: 1,
      release_id: runtime.releaseId ?? "unversioned",
    });
  } catch {
    // A heartbeat failure must not disable the rest of RUM collection.
  }
  recordPendingErrors(client);
  return client;
}

export function initializeCloudWatchRum() {
  initialization ??= createCloudWatchRumClient().catch(() => undefined);
  return initialization;
}

export function setCloudWatchRumPage(pathname: string) {
  if (!rumClient) return;
  recordCurrentPage(rumClient, pathname);
}

export function setCloudWatchRumActor(actor: CloudWatchRumActor) {
  const userCorrelationId = safeObservabilityCorrelationId(
    "user",
    actor.userCorrelationId,
  );
  const role = safeMetadataValue(actor.role);
  if (!userCorrelationId || !role) {
    clearCloudWatchRumActor();
    return;
  }

  const organizationCorrelationId = safeObservabilityCorrelationId(
    "organization",
    actor.organizationCorrelationId,
  );
  const tenantType = safeMetadataValue(actor.tenantType);
  const nextActor: CloudWatchRumActor = {
    userCorrelationId,
    role,
    ...(organizationCorrelationId ? { organizationCorrelationId } : {}),
    ...(tenantType ? { tenantType } : {}),
  };
  const userChanged =
    currentActor?.userCorrelationId !== nextActor.userCorrelationId;
  currentActor = nextActor;
  if (rumClient && userChanged) startRumSession(rumClient);
}

export function clearCloudWatchRumActor() {
  if (!currentActor) return;
  currentActor = undefined;
  if (rumClient) startRumSession(rumClient);
}

export function recordCloudWatchRumError(
  error: unknown,
  context: {
    digest?: string;
    errorId?: string;
    handled?: boolean;
    source: CloudWatchRumErrorSource;
  },
) {
  captureCloudWatchRumError(error, {
    ...context,
    handled: context.handled ?? true,
  });
}

function captureCloudWatchRumError(
  error: unknown,
  context: {
    digest?: string;
    errorId?: string;
    handled: boolean;
    source: CloudWatchRumErrorSource;
  },
  event?: ErrorEventDetail,
) {
  const description = describeClientError(error, event);
  const resolvedErrorId = resolveClientErrorId(error, context.errorId);
  const requestId = getRequestId(error);
  const metadata: EventMetadata = {
    error_handled: context.handled,
    error_id: resolvedErrorId,
    error_source: context.source,
    ...(requestId ? { request_id: requestId } : {}),
  };

  recordSanitizedError(description, metadata);

  const errorName = description.name;
  const systemActionableSource =
    context.source === "app_error_boundary" ||
    context.source === "extension_shell";
  const automaticSource =
    context.source === "window_error" ||
    context.source === "unhandled_rejection";
  const shouldReport =
    errorName === "AbortError"
      ? systemActionableSource
      : errorName === "ChunkLoadError"
        ? systemActionableSource || automaticSource
        : true;
  if (shouldReport) {
    reportCloudWatchClientError(error, description, {
      ...context,
      errorId: resolvedErrorId,
    });
  }
}

function reportCloudWatchClientError(
  error: unknown,
  description: SafeClientError,
  context: {
    source: CloudWatchRumErrorSource;
    errorId: string;
    digest?: string;
    handled: boolean;
  },
) {
  if (typeof window === "undefined") return;
  const errorName = description.name;
  const reportContext =
    errorName === "ChunkLoadError"
      ? "chunk_load_error"
      : context.source === "app_error_boundary" ||
          context.source === "extension_shell"
        ? context.source
        : "rum_error_context";
  if (error !== null && typeof error === "object") {
    const reportedContexts = reportedClientErrorContexts.get(error);
    if (reportedContexts?.has(reportContext)) return;
    if (reportedContexts) reportedContexts.add(reportContext);
    else reportedClientErrorContexts.set(error, new Set([reportContext]));
  }

  const errorId = safeCorrelationId(context.errorId);
  if (!errorId) return;
  const digest = safeCorrelationId(context.digest);
  const requestId = getRequestId(error);
  const browserRelease = nonEmpty(PUBLIC_RUM_ENV.releaseId);
  const payload = {
    source: context.source,
    errorId,
    errorName,
    handled: context.handled,
    ...(description.messageTemplate
      ? { messageTemplate: description.messageTemplate }
      : {}),
    ...description.location,
    ...(description.frames.length
      ? {
          frames: description.frames.slice(
            0,
            CLIENT_ERROR_REPORTED_FRAME_LIMIT,
          ),
        }
      : {}),
    ...(digest ? { digest } : {}),
    ...(requestId ? { requestId } : {}),
    ...(isSafeObservabilityRelease(browserRelease) ? { browserRelease } : {}),
    route: toSafeRumPageId(window.location.pathname),
  };

  try {
    void window
      .fetch("/api/telemetry/client-errors", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        keepalive: true,
      })
      .catch(() => undefined);
  } catch {
    // Error reporting must never change application behavior.
  }
}
